/**
 * Parses an ISO 8601 date string (YYYY-MM-DD) into a Date object in the local timezone.
 * @param dateStr - The date string to parse in YYYY-MM-DD format
 * @returns A Date object representing midnight in the local timezone on that date
 *
 * @throws {Error} If the string is not in YYYY-MM-DD format or represents an invalid date
 */

export const parseLocalDateString = (dateStr: string): Date => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    throw new Error(`Invalid date string format, expected YYYY-MM-DD: "${dateStr}"`);
  }

  const [yearStr, monthStr, dayStr] = dateStr.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    throw new Error(`Invalid numeric components in date string: "${dateStr}"`);
  }
  if (month < 1 || month > 12) {
    throw new Error(`Month out of range in date string: "${dateStr}"`);
  }
  if (day < 1 || day > 31) {
    throw new Error(`Day out of range in date string: "${dateStr}"`);
  }
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() + 1 !== month ||
    parsed.getDate() !== day
  ) {
    throw new Error(`Invalid calendar date in date string: "${dateStr}"`);
  }
  return parsed;
};
